import { updateCartLines } from '@theme/cart-actions';
// `@shopify/events`, not `@theme/events` — `CartErrorEvent` is a STANDARD event and
// lives in the platform bundle. `assets/component-cart-items.js:6-13` splits its imports
// the same way, and getting it wrong is an undefined import, not a resolution error.
import { CartErrorEvent } from '@shopify/events';

/**
 * The cross-sell tile's "toevoegen" control.
 *
 * `docs/canvas/CartDrawer.dc.html` and `docs/canvas/CartOptions.dc.html` both draw an
 * add pill under every cross-sell tile — `1px solid {{a3}}`, `border-radius:999px`,
 * `padding:5px 0`. Nothing in the theme could add a line from inside the drawer, so
 * this is the smallest thing that can.
 *
 * ── WHY NOT REUSE SOMETHING ──────────────────────────────────────────────────
 * `add-to-cart-component` (`assets/product-form.js:33`) only animates; the mutation
 * belongs to `product-form-component` in the same file, which needs a real
 * `{% form 'product' %}` around it. Wiring that into the drawer would put
 * `product-form.js` — the largest module in the cart closure — on every page, to add
 * one line item. ADR-005 §4's budget is the reason this file exists instead.
 *
 * ── COST, MEASURED ───────────────────────────────────────────────────────────
 * One module, 4,603 bytes raw as served (`wc -c`; Shopify serves theme assets
 * byte-for-byte, so the comment block above is part of that figure). Both imports were
 * already in the drawer's graph:
 * `assets/component-cart-items.js` imports `@theme/cart-actions` and `@shopify/events`
 * (lines 6-14) and is loaded by `snippets/cart-products.liquid` on every page the
 * drawer is on. So the marginal cost is this file alone, with no new dependency edges.
 *
 * ── THE MUTATION ─────────────────────────────────────────────────────────────
 * `updateCartLines` is the Standard Action path (`assets/cart-actions.js` — "the one
 * place this theme mutates the cart"). `AGENTS.md` rule 4: no hand-rolled
 * `/cart/add.js`, no exceptions. `origin: this` keeps `event.target` meaning "the
 * component that mutated", which `component-cart-items.js:322` and `cart-drawer.js`
 * both read; because `this` is not the `cart-items-component`, the drawer refreshes
 * itself off the event exactly as it does for an app's mutation.
 *
 * The no-JS route is untouched and lives in Liquid: the pill is a real submit button
 * inside a `<form method="post" action="{{ routes.cart_add_url }}">`, so with this
 * module absent the browser posts the form and lands on the cart page.
 */
class RpCartAdd extends HTMLElement {
  #form = null;
  #button = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    this.#button = this.querySelector('button[type="submit"]');
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #onSubmit = (event) => {
    const form = this.#form;
    if (!form) return;

    const merchandiseId = String(new FormData(form).get('id') ?? '').trim();
    // No id means the browser has nothing useful to post either, so let it through and
    // let the platform answer rather than swallowing the click.
    if (!merchandiseId) return;

    event.preventDefault();
    if (this.#button) this.#button.disabled = true;

    updateCartLines([{ merchandiseId, quantity: 1 }], { action: 'add', context: 'cart', origin: this })
      .then(({ userErrors }) => {
        const message = userErrors[0]?.message;
        if (!message) return;

        // Never announce a platform string raw — 050's i18n amendment. The message is
        // reported for the console; what the shopper hears comes from the listeners'
        // own translated copy.
        this.dispatchEvent(new CartErrorEvent({ error: message, code: userErrors[0]?.code ?? 'INVALID' }));
      })
      .catch((error) => {
        this.dispatchEvent(new CartErrorEvent({ error: error?.message ?? 'Failed to add', code: 'SERVICE_UNAVAILABLE' }));
      })
      .finally(() => {
        if (this.#button) this.#button.disabled = false;
      });
  };
}

if (!customElements.get('rp-cart-add')) {
  customElements.define('rp-cart-add', RpCartAdd);
}
